import { createI18n } from "vue-i18n";
import { createI18nApi } from "#/shared/api/i18n";

// Locale resolution lives in the plain lib module (shared/lib/locale.js) so
// non-composable consumers (e.g. the shared ApiClient) can use it without
// importing this composable; re-exported here for existing callers.
import { DEFAULT_LOCALE, resolveLocale } from "#/shared/lib/locale";

export { resolveLocale };

async function fetchBundles(key, locale) {
  const keys = [].concat(key ?? []);

  return Promise.all(keys.map((bundleKey) => createI18nApi().all(bundleKey, locale)));
}

// Merges each bundle into the instance in order, so a later bundle's value
// wins when two carry the same path.
function mergeBundles(instance, locale, bundles) {
  bundles.forEach((bundle) => instance.global.mergeLocaleMessage(locale, bundle?.toJSON() ?? {}));
}

let i18n;

// `key` names the bundle to load, or an array of bundles merged in order.
export async function useI18n({ locale, key, legacy = false } = {}) {
  const resolvedLocale = resolveLocale(locale);
  const bundles = await fetchBundles(key, resolvedLocale);

  i18n = createI18n({
    legacy,
    locale: resolvedLocale,
    messages: {
      [resolvedLocale]: {},
    },
  });
  mergeBundles(i18n, resolvedLocale, bundles);

  return { i18n };
}

export function useI18nInstance() {
  if (!i18n) {
    throw new Error("i18n instance not initialized");
  }
  return i18n.global;
}

// Upper bound on how long `ready` may gate a component's first paint. A hung
// catalog fetch (one that never settles) must not blank the component forever —
// degraded rendering (raw keys) beats no rendering, and a late fetch still
// hydrates the messages reactively whenever it lands.
const READY_TIMEOUT_MS = 3000;

// Custom elements bootstrap synchronously and can't await `useI18n`. This
// creates the instance immediately and returns a `ready` promise that resolves
// once the strings are hydrated from the i18n endpoint — or after
// READY_TIMEOUT_MS, whichever comes first — letting the element gate first
// paint so labels don't flash their keys.
//
// `fallbackMessages` is the compile-time English catalog for the key (a
// committed app/frontend/src/shared/i18n/frontend.<key>.json — see
// scripts/storybook/generate-i18n.rb), installed under the `en` fallback
// locale: a failed or hung catalog fetch degrades to English copy instead of
// rendering raw snake_case keys.
export function createDeferredI18n({ locale, key, legacy = false, fallbackMessages = {} } = {}) {
  const resolvedLocale = resolveLocale(locale);

  const messages = { [DEFAULT_LOCALE]: fallbackMessages };

  if (resolvedLocale !== DEFAULT_LOCALE) {
    messages[resolvedLocale] = {};
  }

  const instance = createI18n({
    legacy,
    locale: resolvedLocale,
    fallbackLocale: DEFAULT_LOCALE,
    fallbackWarn: false,
    missingWarn: false,
    messages,
  });

  const hydrated = fetchBundles(key, resolvedLocale)
    .then((bundles) => {
      mergeBundles(instance, resolvedLocale, bundles);
    })
    .catch(() => {
      // Network failure leaves the keys in place; non-fatal.
    });

  const ready = Promise.race([hydrated, new Promise((resolve) => setTimeout(resolve, READY_TIMEOUT_MS))]);

  return { i18n: instance, ready };
}
